import { z } from 'zod';
import { Rs485SensorModelSchema } from './Rs485Sensor.js';

export const SensorTypeSchema = z.enum([
  'proximity',
  'temperature',
  'pressure',
  'flow',
  'load_cell',
  'encoder',
  'limit_switch',
  // 2026-06-06: RP2040 smart-sensor classes on RS485. Tag bindings
  // for these come from `Rs485SensorConfig` rather than the legacy
  // sensor_connect path; the type discriminator selects which UI
  // card and reading payload to use.
  'rfid',
  'adc',
  'generic',
]);

export const SensorStatusSchema = z.enum(['online', 'offline', 'degraded', 'discovered']);

export const SensorSchema = z.object({
  id: z.string().min(1),
  project_id: z.string().min(1),
  name: z.string().min(1),
  uuid: z.string().min(1),
  status: SensorStatusSchema,
  sensor_type: SensorTypeSchema,
  sensor_model_id: z.string().nullable(),
  // Parent board the sensor is wired to (typically a sensor_connect board).
  // Nullable for stand-alone / network sensors.
  board_id: z.string().nullable(),
  subsystem_id: z.string().nullable(),
  // RS485 smart-sensor binding, folded in from the retired `Rs485SensorConfig`.
  // Modbus RTU slave id (1..247) for the master `r <sc_id> …` opcode; null when
  // the sensor is not modbus-addressed.
  // `.default(null)` mirrors Rust `#[serde(default)] Option<T>`: a MISSING
  // field decodes as null, so legacy/seeded sensor docs that predate these
  // fields still validate. Output type stays `number | null` (not undefined).
  modbus_slave_id: z.number().int().nullable().default(null),
  // RP2040 smart-sensor chip class the master expects at `modbus_slave_id`;
  // null for non-RS485 / non-RP2040 sensors.
  model: Rs485SensorModelSchema.nullable().default(null),
  // Poll cadence (ms) for RS485 smart sensors; master clamps to [50, 60_000].
  poll_period_ms: z.number().int().nullable().default(null),
  // Channel index (0..15) for multi-channel RP2040 modules; null for single-channel.
  channel: z.number().int().nullable().default(null),
  archived_at_ms: z.number().int().nullable(),
  created_at_ms: z.number().int().nonnegative(),
  updated_at_ms: z.number().int().nonnegative(),
});

export const CreateSensorSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  uuid: z.string().min(1, 'UUID is required'),
  sensor_type: SensorTypeSchema,
  sensor_model_id: z.string().nullable().optional(),
  board_id: z.string().nullable().optional(),
  subsystem_id: z.string().nullable().optional(),
  modbus_slave_id: z.number().int().nullable().optional(),
  model: Rs485SensorModelSchema.nullable().optional(),
  poll_period_ms: z.number().int().nullable().optional(),
  channel: z.number().int().nullable().optional(),
});

export const UpdateSensorSchema = z.object({
  name: z.string().min(1).optional(),
  sensor_type: SensorTypeSchema.optional(),
  sensor_model_id: z.string().nullable().optional(),
  board_id: z.string().nullable().optional(),
  subsystem_id: z.string().nullable().optional(),
  status: SensorStatusSchema.optional(),
  modbus_slave_id: z.number().int().nullable().optional(),
  model: Rs485SensorModelSchema.nullable().optional(),
  poll_period_ms: z.number().int().nullable().optional(),
  channel: z.number().int().nullable().optional(),
});

export type ParsedSensor = z.infer<typeof SensorSchema>;
export type ParsedSensorType = z.infer<typeof SensorTypeSchema>;
export type ParsedSensorStatus = z.infer<typeof SensorStatusSchema>;
export type ParsedCreateSensor = z.infer<typeof CreateSensorSchema>;
export type ParsedUpdateSensor = z.infer<typeof UpdateSensorSchema>;
